import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { db, SyncItem } from '../db';
import { Table, Transaction } from '../types';
import { db as firestore, auth, storage } from '../src/firebase';
import { ref, uploadString, getDownloadURL, listAll, deleteObject } from 'firebase/storage';
import { collection, writeBatch, doc, getDocs, deleteDoc, onSnapshot, query, where, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { useData } from './DataContext';

interface SyncContextType {
    isSyncing: boolean;
    pendingCount: number;
    queueSyncItem: (item: Omit<SyncItem, 'status' | 'timestamp'>) => Promise<void>;
    triggerSync: () => Promise<void>;
    downloadFromCloud: (mode?: 'full' | 'settings_only') => Promise<void>;
    forceUploadData: () => Promise<void>;
    publishQrMenu: (force?: boolean, slugOverride?: string) => Promise<void>;
    deletePublicMenu: (slug: string) => Promise<void>;
    /** SEAL: Yerel pending varsa true - buluttan indirme ASLA yapılmamalı */
    hasLocalPendingSync: () => Promise<boolean>;
}

const SyncContext = createContext<SyncContextType | undefined>(undefined);

export const SyncProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // We use a Ref for internal locking to avoid useEffect dependency cycles,
    // but keep State for UI feedback.
    const [isSyncingState, setIsSyncingState] = useState(false);
    const isSyncingRef = useRef(false);
    const isDownloadingRef = useRef(false); // Mühür: İndirme başladığında set, pending varsa ASLA indirme
    const isDownloadingTransactionsRef = useRef(false); // Transactions-only lock — tables realtime'ı bloklamasın
    const [pendingCount, setPendingCount] = useState(0);

    /** SEAL: Yerelde bekleyen sync var mı? true = buluttan ASLA indirme */
    const hasLocalPendingSync = useCallback(async () => {
        const count = await db.syncStack.where('status').equals('pending').count();
        return count > 0;
    }, []);
    const {
        cafeId, refreshData, setSyncLock,
        dirtyQrProductIds, dirtyQrCategoryIds, dirtyQrModifierGroupIds,
        deletedQrProductIds, deletedQrCategoryIds, deletedQrModifierGroupIds,
        clearDirtyQrLists
    } = useData();
    const { t } = useTranslation();

    // Helper to safely update both Ref and State
    const setSyncing = (status: boolean) => {
        isSyncingRef.current = status;
        setIsSyncingState(status);
    };

    // Monitor pending count only (UI purpose)
    const updatePendingCount = useCallback(async () => {
        const count = await db.syncStack.where('status').equals('pending').count();
        setPendingCount(count);
        return count;
    }, []);

    useEffect(() => {
        updatePendingCount();
        const interval = setInterval(updatePendingCount, 3000);
        return () => clearInterval(interval);
    }, [updatePendingCount]);

    useEffect(() => {
        const handler = () => updatePendingCount();
        window.addEventListener('kahveinn:syncqueued', handler);
        return () => window.removeEventListener('kahveinn:syncqueued', handler);
    }, [updatePendingCount]);

    // Helper to sanitizer data for Firestore (remove undefined)
    const sanitizeData = (data: any): any => {
        if (data === undefined) return null;
        if (data === null) return null;
        if (typeof data !== 'object') return data;
        if (Array.isArray(data)) return data.map(sanitizeData);

        const sanitized: any = {};
        for (const key in data) {
            const val = sanitizeData(data[key]);
            if (val !== undefined) {
                sanitized[key] = val;
            }
        }
        return sanitized;
    };

    const queueSyncItem = async (item: Omit<SyncItem, 'status' | 'timestamp'>) => {
        await db.syncStack.add({
            ...item,
            status: 'pending',
            timestamp: Date.now()
        });
        updatePendingCount();
    };

    const uploadImageToStorage = async (base64: string, path: string): Promise<string> => {
        const imageRef = ref(storage, path);
        await uploadString(imageRef, base64, 'data_url');
        return await getDownloadURL(imageRef);
    };

    // --- Public Menu Sync Helper ---
    const sanitizeForPublic = (type: string, data: any): any => {
        if (!data) return null;

        // 1. Products: Remove Cost, Supplier, Recipe, Stock details
        if (type === 'product') {
            const { cost, supplier, recipe, stock, docType, ...rest } = data;
            return {
                ...rest,
                // We keep 'isAvailable' logic if needed, or mapped from stock > 0
                isAvailable: (stock || 0) > 0
            };
        }

        // 2. Categories: Keep relevant fields
        if (type === 'category') {
            const { id, name, sortOrder, color } = data;
            return { id, name, sortOrder, color };
        }

        // 3. ModifierGroups: Strip internal stock references from options
        if (type === 'modifierGroup') {
            const options = Array.isArray(data.options)
                ? data.options.map(({ stockId, amount, ...rest }: any) => rest)
                : data.options;
            return { ...data, options };
        }

        // 4. Settings: Only QrMenuSettings (mapped to qrsettings key)
        if (type === 'setting' && (data.key === 'qrsettings' || data.key === 'qrMenuSettings')) {
            return data;
        }

        // 5. QR Settings Subcollection
        if (type === 'qrsetting') {
            return data;
        }

        return null; // Skip other types (Transactions, Staff, etc.)
    };
    
    // 'staffs' dahil: personel izin/rol değişiklikleri (örn. sunucu taraflı migration'lar)
    // artık her açık oturuma gerçek zamanlı yansıyor, sadece sayfa yenilenince değil.
    const WATCHED_COLLECTIONS = [
        'tables', 'customers', 'customerAccountMovements', 'staffs'
    ];
    const triggerSync = useCallback(async () => {
        if (!navigator.onLine) return;

        if (!auth.currentUser) {
            console.error("Sync Aborted: No authenticated user.");
            return;
        }

        let activeCafeId = cafeId;

        if (!activeCafeId) {
            activeCafeId = localStorage.getItem('cafeId');
        }

        if (!activeCafeId && auth.currentUser) {
            try {
                const token = await auth.currentUser.getIdTokenResult(true);
                activeCafeId = token.claims.cafeId as string;
                if (activeCafeId) {
                    localStorage.setItem('cafeId', activeCafeId);
                } else {
                    console.error("Sync: 'cafeId' claim missing from auth token.");
                }
            } catch (e) {
                console.error("Sync: Auth token refresh failed:", e);
            }
        }

        if (!activeCafeId) {
            console.error("Sync Aborted: Missing cafeId.", { uid: auth.currentUser?.uid });
            setSyncing(false);
            return;
        }
    
        setSyncing(true);

        let processedIds: number[] = [];

        try {
            let qrSettings = await db.settings.where('key').equals('qrMenuSettings').first();
            if (!qrSettings) {
                qrSettings = await db.settings.where('key').equals('qrsettings').first();
            }

            const appSettings = await db.settings.where('key').equals('appSettings').first();
            const slug = qrSettings?.value?.slug;
            const cafeName = appSettings?.value?.appName || auth.currentUser?.displayName || 'KahveInn Cafe';
    
            const pendingItems = await db.syncStack.where('status').equals('pending').limit(150).toArray();
    
        
    
            if (pendingItems.length === 0) {
                setSyncing(false);
                return;
            }
    
            if (slug) {
                const hasPublicItems = pendingItems.some(i =>
                    ['product', 'category', 'modifierGroup', 'setting'].includes(i.type)
                );
    
                if (hasPublicItems) {
                    try {
                        await setDoc(
                            doc(firestore, `public_menus/${slug}`),
                            {
                                ownerId: auth.currentUser?.uid,
                                cafeName: cafeName,
                                settings: sanitizeData(qrSettings?.value || {}),
                                updatedAt: new Date()
                            },
                            { merge: true }
                        );
                    } catch (e) {
                        console.error("Failed to ensure public menu header:", e);
                    }
                }
            }
    
            const batch = writeBatch(firestore);
            const roleUpdates: Record<string, any> = {};
    
            pendingItems.forEach(item => {
                if (item.type === 'role' && item.data && item.data.id) {
                    roleUpdates[item.data.id] = item.data;
                }
            });
    
            if (Object.keys(roleUpdates).length > 0) {
                const rolesRef = doc(firestore, `cafes/${activeCafeId}/roles/permissions`);
                batch.set(rolesRef, { ...roleUpdates, _syncedAt: new Date() }, { merge: true });
            }
    
            const collectionMap: Record<string, string> = {
                product: 'products',
                stockItem: 'stockItems',
                category: 'categories',
                staff: 'staffs',
                setting: 'settings',
                qrsetting: 'qrsettings',
                modifierGroup: 'modifierGroups',
                role: 'roles',
                zReport: 'zReports',
                shiftReport: 'shiftReports',
                expenseCategory: 'expenseCategories',
                expense: 'expenses',
                taxRate: 'taxRates',
                customer: 'customers',
                customerAccountMovement: 'customerAccountMovements',
                table: 'tables',
                section: 'sections',
                transaction: 'transactions'
            };

            for (const item of pendingItems) {
                if (!item.id) continue;

                processedIds.push(item.id);

                if (item.type === 'role') continue;

                if (!collectionMap[item.type]) {
                    console.warn("Sync Skipped: Unknown collection type", item.type, item);
                    continue;
                }

                // Upload base64 images to Storage before writing to Firestore
                if ((item.action === 'create' || item.action === 'update')) {
                    if (item.type === 'product' && item.data?.image?.startsWith('data:image')) {
                        try {
                            const url = await uploadImageToStorage(
                                item.data.image,
                                `products/${activeCafeId}/${item.data.id}.jpg`
                            );
                            item.data = { ...item.data, image: url };
                            await db.products.update(item.data.id, { image: url });
                        } catch (e) {
                            console.error(`Image upload failed for product "${item.data?.name}":`, e);
                        }
                    }

                    if (item.type === 'staff' && item.data?.avatar?.startsWith('data:image')) {
                        try {
                            const url = await uploadImageToStorage(
                                item.data.avatar,
                                `staff/${activeCafeId}/${item.data.id}.jpg`
                            );
                            item.data = { ...item.data, avatar: url };
                            await db.staff.update(item.data.id, { avatar: url });
                        } catch (e) {
                            console.error(`Avatar upload failed for staff "${item.data?.name}":`, e);
                        }
                    }
                }

                const collectionPath = `cafes/${activeCafeId}/${collectionMap[item.type]}`;

                let docId = item.data?.id ? String(item.data.id) : null;

                if (item.type === 'setting') {
                    if (item.data?.key) {
                        docId = item.data.key;
                    } else {
                        console.warn("Sync Skipped: Setting missing key", item);
                        continue;
                    }
                } else if (item.type === 'qrsetting' && item.data?.key) {
                    docId = item.data.key;
                }

                if (!docId) {
                    docId = doc(collection(firestore, collectionPath)).id;
                }

                const docRef = doc(firestore, collectionPath, docId);

                if (item.action === 'create' || item.action === 'update') {
                    const dataToSync = item.type === 'transaction' && item.data?.items
                        ? { ...item.data, items: item.data.items.map((i: any) => ({ ...i, image: null })) }
                        : item.data;
                    const safeData = sanitizeData(dataToSync);
                    batch.set(docRef, { ...safeData, _syncedAt: new Date() }, { merge: true });
                } else if (item.action === 'delete') {
                    batch.delete(docRef);
                }
            }
    
            await batch.commit();
    
            await db.syncStack.bulkDelete(processedIds);
    
            const remaining = await db.syncStack.where('status').equals('pending').count();
            setPendingCount(remaining);
    
        } catch (error: any) {
            console.error("Sync Failed:", error);
            if (processedIds.length > 0) {
                await Promise.all(
                    processedIds.map(async (id) => {
                        const item = await db.syncStack.get(id);
                        if (!item) return;
                        const newCount = (item.retryCount ?? 0) + 1;
                        if (newCount >= 3) {
                            await db.syncStack.update(id, { status: 'failed', retryCount: newCount });
                        } else {
                            await db.syncStack.update(id, { retryCount: newCount });
                        }
                    })
                );
                const remaining = await db.syncStack.where('status').equals('pending').count();
                setPendingCount(remaining);
            }
        } finally {
            setSyncing(false);
        }
    }, [cafeId, t]);

    // ... (downloadFromCloud and forceUploadData remain) ...

    // ... queueSyncItem is already defined above ...

    // Auto-Sync Logic: PURE DEBOUNCE (Reset timer on every change)
    useEffect(() => {
        if (pendingCount === 0) return;

        // If we have items, wait 5 seconds of "silence" before syncing
        // Every time pendingCount changes (new item added), this effect re-runs,
        // clearing the old timer and starting a new one.
        const timer = setTimeout(() => {
            if (!isSyncingRef.current && navigator.onLine) {
                triggerSync();
            }
        }, 5000); // 5 Seconds Debounce

        return () => clearTimeout(timer);
    }, [pendingCount, triggerSync]);

    // Fallback Polling (Only updates UI count, does NOT trigger sync anymore)
    // This serves as a heartbeat and helps if something gets stuck, 
    // but we rely on the debounce for the primary trigger.

    const downloadFromCloud = useCallback(async () => {
        if (isSyncingRef.current) return;
        if (isDownloadingRef.current) return;
        if (isDownloadingTransactionsRef.current) return;

        // MÜHÜR #1: Pending veya failed varsa KESİN return - yerel veri kutsaldır
        const actualPending = await db.syncStack.where('status').anyOf('pending', 'failed').count();
        if (actualPending > 0) {
            return;
        }

        if (!navigator.onLine) {
            alert(t('settings.sync.alerts.offline'));
            return;
        }

        const activeCafeId = cafeId || localStorage.getItem('cafeId');
        if (!activeCafeId) {
            alert(t('settings.sync.alerts.cafe_id_missing'));
            return;
        }

        isDownloadingRef.current = true;
        setSyncing(true);

        try {
            // 1. Products & Stock Items
            const prodsRef = collection(firestore, `cafes/${activeCafeId}/products`);
            const prodsSnap = await getDocs(prodsRef);

            const rawProducts: any[] = [];
            let rawStocks: any[] = [];

            prodsSnap.forEach(doc => {
                const data = doc.data();
            
                const hasProductFields =
                    typeof data?.name === 'string' &&
                    data?.category !== undefined &&
                    data?.price !== undefined;
            
                if (data.docType === 'product') {
                    rawProducts.push({
                        ...data,
                        cost: undefined,
                        unit: undefined,
                        stock: undefined,
                        supplier: undefined,
                        docType: 'product'
                    });
                    return;
                }
            
                if (data.docType === 'stockItem') {
                    return;
                }
            
                if (hasProductFields) {
                    rawProducts.push({
                        ...data,
                        docType: 'product'
                    });
                    return;
                }
            
                console.warn('Skipped non-product document in products collection:', {
                    id: doc.id,
                    data
                });
            });

            // 1.5. Stock Items
            const stockRef = collection(firestore, `cafes/${activeCafeId}/stockItems`);
            const stockSnap = await getDocs(stockRef);
            const stockById = new Map<string, any>();
            const toMs = (value: any): number => {
                if (!value) return 0;
                if (typeof value?.toMillis === 'function') return value.toMillis();
                const parsed = new Date(value).getTime();
                return Number.isFinite(parsed) ? parsed : 0;
            };
            stockSnap.forEach(doc => {
                const data = doc.data();
                const idKey = data?.id !== undefined && data?.id !== null ? String(data.id) : doc.id;
                const normalized = { ...data, docType: 'stockItem' as const };
                const existing = stockById.get(idKey);
                if (!existing) {
                    stockById.set(idKey, normalized);
                    return;
                }

                const nextMs = toMs((normalized as any)?._syncedAt);
                const prevMs = toMs((existing as any)?._syncedAt);
                if (nextMs > prevMs) {
                    stockById.set(idKey, normalized);
                    return;
                }

                if (nextMs === prevMs) {
                    const nextJson = JSON.stringify(normalized);
                    const prevJson = JSON.stringify(existing);
                    if (nextJson > prevJson) {
                        stockById.set(idKey, normalized);
                    }
                }
            });
            rawStocks = Array.from(stockById.values());

            // 2. Categories
            let rawCats: any[] = [];
            const catsRef1 = collection(firestore, `cafes/${activeCafeId}/categorys`);
            const catsSnap1 = await getDocs(catsRef1);
            if (catsSnap1.size > 0) rawCats = [...rawCats, ...catsSnap1.docs.map(d => d.data())];

            const catsRef2 = collection(firestore, `cafes/${activeCafeId}/categories`);
            const catsSnap2 = await getDocs(catsRef2);
            if (catsSnap2.size > 0) {
                const existingIds = new Set(rawCats.map(c => c.id));
                catsSnap2.docs.forEach(d => {
                    const data = d.data();
                    if (!existingIds.has(data.id)) rawCats.push(data);
                });
            }

            // 3. Others
            const staffRef = collection(firestore, `cafes/${activeCafeId}/staffs`);
            const staffSnap = await getDocs(staffRef);
            const rawStaff = staffSnap.docs.map(d => {
                const { pinCode, ...rest } = d.data();
                return { ...rest, hasPinCode: !!pinCode };
            });

            const transRef = collection(firestore, `cafes/${activeCafeId}/transactions`);
const transSnap = await getDocs(transRef);
const rawTrans = transSnap.docs.map(d => d.data());

const zRef = collection(firestore, `cafes/${activeCafeId}/zReports`);
const zSnap = await getDocs(zRef);
const rawZ = zSnap.docs.map(d => d.data());

const shiftRef = collection(firestore, `cafes/${activeCafeId}/shiftReports`);
const shiftSnap = await getDocs(shiftRef);
const rawShifts = shiftSnap.docs.map(d => d.data());

            const modRef = collection(firestore, `cafes/${activeCafeId}/modifierGroups`);
            const modSnap = await getDocs(modRef);
            const rawModifiers = modSnap.docs.map(d => d.data());

            const expCatRef = collection(firestore, `cafes/${activeCafeId}/expenseCategories`);
            const expCatSnap = await getDocs(expCatRef);
            const rawExpCats = expCatSnap.docs.map(d => d.data());

            const expRef = collection(firestore, `cafes/${activeCafeId}/expenses`);
            const expSnap = await getDocs(expRef);
            const rawExpenses = expSnap.docs.map(d => d.data());

            const taxRef = collection(firestore, `cafes/${activeCafeId}/taxRates`);
            const taxSnap = await getDocs(taxRef);
            const rawTaxes = taxSnap.docs.map(d => d.data());

            const tablesRef = collection(firestore, `cafes/${activeCafeId}/tables`);
            const tablesSnap = await getDocs(tablesRef);
            const rawTables = tablesSnap.docs.map(d => d.data());

            const sectionsRef = collection(firestore, `cafes/${activeCafeId}/sections`);
            const sectionsSnap = await getDocs(sectionsRef);
            const rawSections = sectionsSnap.docs.map(d => d.data());

            // 4. Customers (CRM)
            const custRef = collection(firestore, `cafes/${activeCafeId}/customers`);
            const custSnap = await getDocs(custRef);
            const rawCustomers = custSnap.docs.map(d => d.data());
            const camRef = collection(firestore, `cafes/${activeCafeId}/customerAccountMovements`);
            const camSnap = await getDocs(camRef);
            const rawCustomerAccountMovements = camSnap.docs.map(d => d.data());

            // 5. Roles (Singleton Map)
            const roleRef = doc(firestore, `cafes/${activeCafeId}/roles/permissions`);
            const roleSnap = await getDoc(roleRef);
            const rawRoles: any[] = [];
            if (roleSnap.exists()) {
                const roleData = roleSnap.data();
                // Parse the map: keys are IDs, values are Role objects
                Object.values(roleData).forEach((val: any) => {
                    if (typeof val === 'object' && val.id) {
                        rawRoles.push(val);
                    }
                });
            }

            // 5. QR Settings (Subcollection)
            const rawSettings: any[] = []; // Fix: Initialize rawSettings
            const qrSetRef = collection(firestore, `cafes/${activeCafeId}/qrsettings`);
            const qrSetSnap = await getDocs(qrSetRef);
            qrSetSnap.forEach(doc => {
                // We treat this as a 'setting' type locally
                // The doc ID should be 'qrsettings'
                rawSettings.push(doc.data());
            });

            // 6. App Settings (CRITICAL FIX: Fetch Main Settings)
            const settingsRef = collection(firestore, `cafes/${activeCafeId}/settings`);
            const settingsSnap = await getDocs(settingsRef);

const settingsByKey = new Map<string, any>();

settingsSnap.forEach(docSnap => {
    const data = docSnap.data();

    let effectiveKey = data.key;
    if (!effectiveKey && (docSnap.id === 'appSettings' || docSnap.id === 'qrMenuSettings' || docSnap.id === 'loyaltySettings' || docSnap.id === 'categoryTaxMap')) {
        effectiveKey = docSnap.id;
    }

    if (!effectiveKey) return;

    const candidate = { ...data, key: effectiveKey };
    const existing = settingsByKey.get(effectiveKey);

    if (!existing) {
        settingsByKey.set(effectiveKey, candidate);
        return;
    }

    const candidateIsCanonical = docSnap.id === effectiveKey;
    const existingIsCanonical = existing.__docId === effectiveKey;

    if (candidateIsCanonical && !existingIsCanonical) {
        settingsByKey.set(effectiveKey, { ...candidate, __docId: docSnap.id });
        return;
    }

    const candidateSyncedAt = data._syncedAt?.toMillis?.() ?? new Date(data._syncedAt || 0).getTime?.() ?? 0;
    const existingSyncedAt = existing._syncedAt?.toMillis?.() ?? new Date(existing._syncedAt || 0).getTime?.() ?? 0;

    if (candidateSyncedAt >= existingSyncedAt) {
        settingsByKey.set(effectiveKey, { ...candidate, __docId: docSnap.id });
    }
});

settingsByKey.forEach(value => {
    const { __docId, ...cleanValue } = value;
    rawSettings.push(cleanValue);
});

            // MÜHÜR #2: Yazmadan hemen önce tekrar kontrol - arada pending eklendiyse YAZMA
            const recheckPending = await db.syncStack.where('status').equals('pending').count();
            if (recheckPending > 0) {
                return;
            }

            // Apply updates
            setSyncLock(true, 'cloud-download');
            await db.transaction('rw', [db.products, db.stockItems, db.categories, db.staff, db.transactions, db.zReports, db.shiftReports, db.modifierGroups, db.settings, db.expenses, db.expenseCategories, db.taxRates, db.restaurantTables, db.sections, db.customers, db.customerAccountMovements,
                 db.roles], async () => {
                if (rawProducts.length > 0) { await db.products.clear(); await db.products.bulkPut(rawProducts as any); }
                if (rawStocks.length > 0) { await db.stockItems.clear(); await db.stockItems.bulkPut(rawStocks as any); }
                if (rawCats.length > 0) { await db.categories.clear(); await db.categories.bulkPut(rawCats as any); }
                if (rawStaff.length > 0) { await db.staff.clear(); await db.staff.bulkPut(rawStaff as any); }
                if (rawTrans.length > 0) { await db.transactions.clear(); await db.transactions.bulkPut(rawTrans as any); }
                if (rawZ.length > 0) { await db.zReports.clear(); await db.zReports.bulkPut(rawZ as any); }
                if (rawShifts.length > 0) { await db.shiftReports.clear(); await db.shiftReports.bulkPut(rawShifts as any); }
                if (rawModifiers.length > 0) { await db.modifierGroups.clear(); await db.modifierGroups.bulkPut(rawModifiers as any); }
                if (rawExpCats.length > 0) { await db.expenseCategories.clear(); await db.expenseCategories.bulkPut(rawExpCats as any); }
                if (rawExpenses.length > 0) { await db.expenses.clear(); await db.expenses.bulkPut(rawExpenses as any); }
                if (rawTaxes.length > 0) { await db.taxRates.clear(); await db.taxRates.bulkPut(rawTaxes as any); }
                if (rawTables.length > 0) { const existingTables = await db.restaurantTables.toArray(); const existingMap = new Map(existingTables.map((t: any) => [t.id, t._updatedAt ?? 0])); const tablesToWrite = (rawTables as any[]).filter(t => (t._updatedAt ?? 0) >= (existingMap.get(t.id) ?? 0)); await db.restaurantTables.clear(); if (tablesToWrite.length > 0) await db.restaurantTables.bulkPut(tablesToWrite); }
                if (rawSections.length > 0) { await db.sections.clear(); await db.sections.bulkPut(rawSections as any); }
                if (rawCustomers.length > 0) { await db.customers.clear(); await db.customers.bulkPut(rawCustomers as any); }
                if (rawCustomerAccountMovements.length > 0) { await db.customerAccountMovements.clear(); await db.customerAccountMovements.bulkPut(rawCustomerAccountMovements as any); }
                if (rawRoles.length > 0) { await db.roles.clear(); await db.roles.bulkPut(rawRoles as any); }
                if (rawSettings.length > 0) { await db.settings.clear(); await db.settings.bulkPut(rawSettings as any); }
            });
            setSyncLock(false);
            await refreshData({ fromCloud: true });
            // alert(t('settings.sync.alerts.download_success', { count: products.length }));

        } catch (error: any) {
            console.error("Download Failed:", error);
            alert(t('settings.sync.alerts.download_error') + error.message);
        } finally {
            isDownloadingRef.current = false;
            setSyncing(false);
        }
    }, [cafeId, refreshData, t]);

    const forceUploadData = async () => {
        if (isSyncingRef.current) return;
        setSyncing(true);
    
        try {
    
            const timestamp = Date.now();
            const stackItems: any[] = [];
    
            const products = await db.products.toArray();
            const stocks = await db.stockItems.toArray();
            const categories = await db.categories.toArray();
            const staff = await db.staff.toArray();
            const modifierGroups = await db.modifierGroups.toArray();
            const settings = await db.settings.toArray();
            const expenseCategories = await db.expenseCategories.toArray();
            const expenses = await db.expenses.toArray();
            const taxRates = await db.taxRates.toArray();
            const tables = await db.restaurantTables.toArray();
            const sections = await db.sections.toArray();
            const customers = await db.customers.toArray();
    
            products.forEach(p => stackItems.push({ type: 'product', action: 'create', data: p, status: 'pending', timestamp }));
            stocks.forEach(s => stackItems.push({ type: 'stockItem', action: 'create', data: s, status: 'pending', timestamp }));
            categories.forEach(c => stackItems.push({ type: 'category', action: 'create', data: c, status: 'pending', timestamp }));
            staff.forEach(s => stackItems.push({ type: 'staff', action: 'create', data: s, status: 'pending', timestamp }));
            modifierGroups.forEach(m => stackItems.push({ type: 'modifierGroup', action: 'create', data: m, status: 'pending', timestamp }));
    
            const settingsToSync = settings.filter(s =>
                ['appSettings', 'loyaltySettings', 'qrMenuSettings', 'categoryTaxMap'].includes(s.key)
            );
            settingsToSync.forEach(s =>
                stackItems.push({ type: 'setting', action: 'update', data: s, status: 'pending', timestamp })
            );
    
            const qrSettings = settings.find(s => s.key === 'qrsettings');
            if (qrSettings) {
                stackItems.push({ type: 'qrsetting', action: 'update', data: qrSettings, status: 'pending', timestamp });
            }
    
            expenseCategories.forEach(c => stackItems.push({ type: 'expenseCategory', action: 'create', data: c, status: 'pending', timestamp }));
            expenses.forEach(e => stackItems.push({ type: 'expense', action: 'create', data: e, status: 'pending', timestamp }));
            taxRates.forEach(t => stackItems.push({ type: 'taxRate', action: 'create', data: t, status: 'pending', timestamp }));
            tables.forEach(t => stackItems.push({ type: 'table', action: 'create', data: t, status: 'pending', timestamp }));
            sections.forEach(s => stackItems.push({ type: 'section', action: 'create', data: s, status: 'pending', timestamp }));
            customers.forEach(c => stackItems.push({ type: 'customer', action: 'create', data: c, status: 'pending', timestamp }));
    
            await db.syncStack.bulkAdd(stackItems);
            await updatePendingCount();
            await triggerSync();
    
        } catch (error) {
            console.error("Force Upload Failed:", error);
            alert(t('settings.sync.alerts.prep_fail'));
        } finally {
            setSyncing(false);
            setSyncLock(false);
        }
    };

    const publishQrMenu = async (force: boolean = false, slugOverride?: string) => {
        if (isSyncingRef.current) return;
        setSyncing(true);
    
        try {
            // 1. Get Settings & Slug
            const appSettings = await db.settings.where('key').equals('appSettings').first();
    
            let qrSettings = await db.settings.where('key').equals('qrMenuSettings').first();
            if (!qrSettings) {
                qrSettings = await db.settings.where('key').equals('qrsettings').first();
            }

            const slug = slugOverride || qrSettings?.value?.slug;
            if (!slug) {
                alert("Lütfen önce bir mağaza linki belirleyin.");
                setSyncing(false);
                return;
            }
            let activeCafeId = cafeId || localStorage.getItem('cafeId');

            if (!activeCafeId && auth.currentUser) {
                try {
                    const token = await auth.currentUser.getIdTokenResult(true);
                    activeCafeId = (token.claims.cafeId as string) || null;
                    if (activeCafeId) {
                        localStorage.setItem('cafeId', activeCafeId);
                    }
                } catch (e) {
                    console.error('Publish: CafeID token recovery failed:', e);
                }
            }
            
            if (!activeCafeId) {
                throw new Error('Publish aborted: missing cafeId');
            }
            const cafeName = appSettings?.value?.appName || auth.currentUser?.displayName || 'KahveInn Cafe';

            // Ensure Parent Document Exists BEFORE Batch
            // Security rules check get(parent) which must exist before the batch starts
            try {
                await setDoc(doc(firestore, `public_menus/${slug}`), {
                    ownerId: auth.currentUser?.uid,
                    cafeId: activeCafeId,
                    cafeName: cafeName,
                    updatedAt: new Date(),
                    themeColor: qrSettings.value?.themeColor || '#22c55e',
                    headerText: qrSettings.value?.headerText || '',
                    isActive: qrSettings.value?.isActive ?? true,
                    allowOrdering: qrSettings.value?.allowOrdering ?? true,
                    soundEnabled: qrSettings.value?.soundEnabled ?? false
                }, { merge: true });
            } catch (e) {
                console.error("Publish: Failed to ensure parent document:", e);
                throw e;
            }

            // 2. Prepare Batch for Legacy Sync (Keep this for backward compatibility if needed, or minimal updates)
            const batch = writeBatch(firestore);

            // --- A. Metadata Update (Redundant but harmless in batch) ---
            const rootRef = doc(firestore, `public_menus/${slug}`);
            batch.set(rootRef, {
                ownerId: auth.currentUser?.uid,
                cafeId: activeCafeId,
                cafeName: cafeName,
                updatedAt: new Date(),
                themeColor: qrSettings.value?.themeColor || '#22c55e',
                headerText: qrSettings.value?.headerText || '',
                isActive: qrSettings.value?.isActive ?? true,
                allowOrdering: qrSettings.value?.allowOrdering ?? true,
                soundEnabled: qrSettings.value?.soundEnabled ?? false
            }, { merge: true });

            // --- B. OPTIMIZED BUNDLE GENERATION ---
            // 1. Fetch EVERYTHING needed for the bundle (regardless of dirty state)
            const rawProducts = await db.products.toArray();
            const allProducts = rawProducts.filter((p: any) => p.isArchived !== true && p.isArchived !== 'true');
            const allCategories = await db.categories.toArray();
            const allModifiers = await db.modifierGroups.toArray();

            // 2. Image Optimization (Base64 -> Storage URL)
            const optimizedProducts: any[] = [];
            let imageUploadCount = 0;

            for (const p of allProducts) {
                let imageUrl = p.image;

                // LOGIC:
                // 1. If we have a publicImageUrl and the product is NOT dirty, use it.
                // 2. If dirty OR no publicUrl, we must upload the Base64 (if exists).

                const isDirty = dirtyQrProductIds.has(p.id!);
                const hasPublicUrl = !!p.publicImageUrl;

                if (hasPublicUrl && !isDirty) {
                    imageUrl = p.publicImageUrl;
                } else if (p.image && p.image.startsWith('data:image')) {
                    // Upload required!
                    try {
                        const imageRef = ref(storage, `public_menus/${slug}/images/${p.id}`);
                        await uploadString(imageRef, p.image, 'data_url');
                        const newUrl = await getDownloadURL(imageRef);

                        imageUrl = newUrl;
                        imageUploadCount++;

                        // OPTIMIZATION: Save the public URL to a separate field
                        // DO NOT overwrite 'image' (keep Base64 for POS)
                        await db.products.update(p.id!, { publicImageUrl: newUrl });
                    } catch (e) {
                        console.error(`Failed to upload image for product ${p.name}:`, e);
                    }
                }

                // Add to optimized list (Sanitized & Safe)
                // We use 'imageUrl' which is now the HTTPS link (or base64 if upload failed)
                const publicP = sanitizeForPublic('product', { ...p, image: imageUrl });
                if (publicP) optimizedProducts.push(sanitizeData(publicP));
            }

            // 3. Sanitize Categories & Modifiers
            const publicCategories = allCategories
                .map(c => sanitizeForPublic('category', c))
                .filter(c => c !== null)
                .map(c => sanitizeData(c));

            const publicModifiers = allModifiers
                .map(m => sanitizeForPublic('modifierGroup', m))
                .filter(m => m !== null)
                .map(m => sanitizeData(m));

            // 4. Create Bundle Object
            const bundleData = {
                settings: sanitizeData(qrSettings?.value || {}),
                cafeName,
                products: optimizedProducts,
                categories: publicCategories,
                modifierGroups: publicModifiers,
                generatedAt: new Date().toISOString()
            };

            // 5. Write Bundle to Firestore (Subcollection 'data', doc 'menu_v2')
            const bundleRef = doc(firestore, `public_menus/${slug}/data/menu_v2`);
            batch.set(bundleRef, bundleData, { merge: true });


            // --- C. Legacy Sync Execution (Optional: maintain for older app versions if any) ---
            // For now, we CONTINUING legacy sync to be safe, but the bundle is the source of truth for V2
            // 1. Process Deletions (Graveyard Strategy)
            const processDeletions = (collName: string, deletedIds: Set<any>) => {
                if (deletedIds.size === 0) return;
                deletedIds.forEach(id => {
                    const docRef = doc(firestore, `public_menus/${slug}/${collName}`, String(id));
                    batch.delete(docRef);
                });
            };

            // 2. Smart Sync Collections (Updates/Creates)
            const syncSmartCollection = async (collName: string, idsToSync: Set<any>, localTable: any, type: string) => {
                if (idsToSync.size === 0) return;

                const idArray = Array.from(idsToSync);
                // Chunking for safety (though batch limit is 500, bulkGet is local)
                const localItems = await localTable.bulkGet(idArray);
                let opCount = 0;

                for (let i = 0; i < idArray.length; i++) {
                    const id = idArray[i];
                    const item = localItems[i];
                    const docId = String(id);
                    const docRef = doc(firestore, `public_menus/${slug}/${collName}`, docId);

                    if (item) {
                        // UPDATE / CREATE
                        const publicData = sanitizeForPublic(type, item);
                        if (publicData) {
                            const safeData = sanitizeData(publicData);
                            batch.set(docRef, { ...safeData, _syncedAt: new Date() }, { merge: true });
                            opCount++;
                        }
                    } else {
                        batch.delete(docRef);
                    }
                }
            };

            let productsToSync = dirtyQrProductIds;
            let catsToSync = dirtyQrCategoryIds;
            let modsToSync = dirtyQrModifierGroupIds;

            if (force) {
                const allProdIds = await db.products.toCollection().primaryKeys();
                const allCatIds = await db.categories.toCollection().primaryKeys();
                const allModIds = await db.modifierGroups.toCollection().primaryKeys();

                productsToSync = new Set(allProdIds as number[]);
                catsToSync = new Set(allCatIds as string[]);
                modsToSync = new Set(allModIds as number[]);
            }

            // 1. Deletions First (Clear the way)
            processDeletions('products', deletedQrProductIds);
            processDeletions('categories', deletedQrCategoryIds);
            processDeletions('modifierGroups', deletedQrModifierGroupIds);

            // 2. Updates Next
            await Promise.all([
                syncSmartCollection('products', productsToSync, db.products, 'product'),
                syncSmartCollection('categories', catsToSync, db.categories, 'category'),
                syncSmartCollection('modifierGroups', modsToSync, db.modifierGroups, 'modifierGroup')
            ]);


            // --- D. Commit ---
            await batch.commit();

            // --- E. Cleanup ---
            clearDirtyQrLists();

            alert(t('common.saved', 'Menünüz başarıyla yayınlandı!'));

        } catch (error) {
            console.error("Publish QR Menu Failed:", error);
            alert("Menü yayınlanırken bir hata oluştu: " + (error as any).message);
        } finally {
            setSyncing(false);
        }
    };

    const deletePublicMenu = async (slug: string) => {
        if (!slug) return;
        setSyncing(true);

        try {
            // 1. Delete Images from Storage
            try {
                const listRef = ref(storage, `public_menus/${slug}/images`);
                const res = await listAll(listRef);
                await Promise.all(res.items.map((itemRef) => deleteObject(itemRef)));
            } catch (e) {
                console.warn("[Cleanup] Storage cleanup failed (might be empty or missing permissions):", e);
            }

            // 2. Delete Firestore Data (Subcollections + Parent)
            // Note: Client SDK cannot delete collections recursively. We must enumerate.
            const subCollections = ['products', 'categories', 'modifierGroups', 'data', 'categorys'];

            // We use a simplified approach: List all docs in subcollections and batch delete them.
            // If >500 items, we might need multiple batches.

            const batch = writeBatch(firestore);
            let operationCount = 0;

            for (const subCol of subCollections) {
                const colRef = collection(firestore, `public_menus/${slug}/${subCol}`);
                const snap = await getDocs(colRef);

                snap.forEach(doc => {
                    batch.delete(doc.ref);
                    operationCount++;
                });
            }

            // Delete Parent Document
            const parentRef = doc(firestore, `public_menus/${slug}`);
            batch.delete(parentRef);
            await batch.commit();
        } catch (error) {
            console.error("[Cleanup] Failed:", error);
            // We don't throw here to avoid blocking the user flow if cleanup fails
        } finally {
            setSyncing(false);
        }
    };

    useEffect(() => {
        const safeTrigger = async () => {
            const count = await updatePendingCount();
            if (count > 0 && !isSyncingRef.current && navigator.onLine) {
                triggerSync();
            }
        };
    
        safeTrigger();
    }, []);
     // Empty dependency array to run ONLY on mount/unmount. 
    // We trust queueSyncItem to update pendingCount, and the interval to catch it.
    // If we want "Debounced" reaction to queueSyncItem, we should add a separate effect or use a ref.

useEffect(() => {
    if (!cafeId) return;

    const downloadTransactions = async () => {
        if (isSyncingRef.current || isDownloadingRef.current || isDownloadingTransactionsRef.current) return;
        const activeCafeId = cafeId || localStorage.getItem('cafeId');
        if (!activeCafeId) return;
        isDownloadingTransactionsRef.current = true;
        try {
            const since = new Date();
            since.setDate(since.getDate() - 30);
            const snapshot = await getDocs(
                query(
                    collection(firestore, `cafes/${activeCafeId}/transactions`),
                    where('date', '>=', since.toISOString())
                )
            );
            const rawTransactions = snapshot.docs.map(d => d.data());
            await db.transactions.bulkPut(rawTransactions as any);
            refreshData();
        } catch (e) {
            console.error('transactions sync error', e);
        } finally {
            isDownloadingTransactionsRef.current = false;
        }
    };

    // Tek doküman değişikliğinde tüm collection'ı yeniden indirmek yerine,
    // Firestore'un zaten sağladığı docChanges() ile sadece değişen dokümanı Dexie'ye yansıt.
    const applyRealtimeDocChanges = async (
        snapshot: any,
        table: any,
        toKey: (id: string) => any = (id) => id,
        transformDoc: (data: any) => any = (data) => data
    ) => {
        if (isSyncingRef.current || isDownloadingRef.current) return;

        // SEAL/MÜHÜR: Yerelde pending varsa buluttan gelen değişikliği uygulama
        const hasPending = await db.syncStack.where('status').equals('pending').count();
        if (hasPending > 0) return;

        for (const change of snapshot.docChanges()) {
            if (change.type === 'added' || change.type === 'modified') {
                await table.put(transformDoc(change.doc.data()));
            } else if (change.type === 'removed') {
                await table.delete(toKey(change.doc.id));
            }
        }
    };

    const unsubTables = onSnapshot(
        collection(firestore, `cafes/${cafeId}/tables`),
        async (snapshot) => {
            if (snapshot.metadata.hasPendingWrites) return;
            if (isSyncingRef.current || isDownloadingRef.current) return;

            const hasPending = await db.syncStack.where('status').equals('pending').count();
            if (hasPending > 0) return;

            for (const change of snapshot.docChanges()) {
                if (change.type === 'added' || change.type === 'modified') {
                    const data = change.doc.data();
                    const existing = await db.restaurantTables.get(data.id);
                    if (existing && ((data as any)._updatedAt ?? 0) < ((existing as any)._updatedAt ?? 0)) {
                        continue;
                    }
                    await db.restaurantTables.put(data as Table);
                } else if (change.type === 'removed') {
                    await db.restaurantTables.delete(change.doc.data().id);
                }
            }

            await refreshData();
        }
    );

    const unsubCustomers = onSnapshot(
        collection(firestore, `cafes/${cafeId}/customers`),
        (snapshot) => { applyRealtimeDocChanges(snapshot, db.customers); }
    );

    const unsubCustomerAccountMovements = onSnapshot(
        collection(firestore, `cafes/${cafeId}/customerAccountMovements`),
        (snapshot) => { applyRealtimeDocChanges(snapshot, db.customerAccountMovements); }
    );

    const unsubStaff = onSnapshot(
        collection(firestore, `cafes/${cafeId}/staffs`),
        (snapshot) => {
            applyRealtimeDocChanges(snapshot, db.staff, (id) => Number(id), (data) => {
                const { pinCode, ...rest } = data;
                return { ...rest, hasPinCode: !!pinCode };
            });
        }
    );

    const unsubTransactions = onSnapshot(
        collection(firestore, `cafes/${cafeId}/transactions`),
        async (snapshot) => {
            // Bu cihazda yapılan local yazımın yankısı - başka cihazdan gelen değişiklik değil
            if (snapshot.metadata.hasPendingWrites) return;
            if (isSyncingRef.current || isDownloadingTransactionsRef.current) return;

            const since = new Date();
            since.setDate(since.getDate() - 30);

            let needsFullRefetch = false;

            for (const change of snapshot.docChanges()) {
                if (change.type === 'added') {
                    const data = change.doc.data();
                    if (data?.date && new Date(data.date) >= since) {
                        await db.transactions.put(data as Transaction);
                    }
                } else {
                    // modified/removed: Z-raporu kapanışı veya void gibi kompleks güncellemeler - tam re-fetch daha güvenli
                    needsFullRefetch = true;
                }
            }

            if (needsFullRefetch) {
                downloadTransactions();
            }
        }
    );

    return () => {
        unsubTables();
        unsubCustomers();
        unsubCustomerAccountMovements();
        unsubStaff();
        unsubTransactions();
    };
}, [cafeId]);
useEffect(() => {
    if (!cafeId) return;

    const runInitialDownload = async () => {
        try {
            if (isSyncingRef.current || isDownloadingRef.current) return;

            const hasPending = await db.syncStack
                .where('status')
                .equals('pending')
                .count();

            if (hasPending > 0) return;

            await downloadFromCloud();
        } catch (error) {
            console.error("Initial download error:", error);
        }
    };

    runInitialDownload();
}, [cafeId]);
const value = {
    
    isSyncing: isSyncingState,
    pendingCount,
    triggerSync,
    downloadFromCloud,
    forceUploadData,
    queueSyncItem,
    publishQrMenu,
    deletePublicMenu,
    hasLocalPendingSync
};

return (
    <SyncContext.Provider value={value}>
        {children}
    </SyncContext.Provider>
);
};

export const useSync = () => {
const context = useContext(SyncContext);
if (!context) {
    throw new Error('useSync must be used within a SyncProvider');
}
return context;
};
